/**
 * GameHeader Component
 * 
 * Top header with MyMinu logo, hamburger menu, and profile avatar.
 * Cream background with rounded bottom corners.
 */

import React from 'react';
import { Profile } from '../../types';
import styles from './GameHeader.module.scss';
import { Logo } from '../Branding/Logo';

interface GameHeaderProps {
  onMenuClick: () => void;
  onProfileClick?: () => void;
  activeProfile?: Profile | null;
}

export const GameHeader: React.FC<GameHeaderProps> = ({ 
  onMenuClick,
  onProfileClick,
  activeProfile,
}) => {
  // Get initials from profile name
  const getInitials = (name: string) => {
    return name.charAt(0).toUpperCase();
  };

  // The avatar and the old hamburger both opened the same drawer (menu + profile
  // switcher), so they were redundant. We keep a single control on the right:
  // the profile avatar, which opens the menu. Prefer onMenuClick, falling back to
  // onProfileClick, so the drawer opens regardless of which handler is wired.
  const openMenu = onMenuClick ?? onProfileClick;

  return (
    <header className={styles.header}>
      {/* Spacer on the left keeps the centered logo balanced against the avatar */}
      <div className={styles.placeholder} />

      {/* Logo in center */}
      <div className={styles.logo}>
        <Logo size="medium" variant="black" />
      </div>

      {/* Single menu/profile control on the right */}
      {activeProfile ? (
        <button
          className={styles.profileButton}
          onClick={openMenu}
          aria-label={`Menu — signed in as ${activeProfile.display_name}`}
          type="button"
        >
          <div className={styles.profileAvatar}>
            {getInitials(activeProfile.display_name)}
            <span className={styles.menuHint} aria-hidden="true">
              <span className={styles.menuHintLine} />
              <span className={styles.menuHintLine} />
            </span>
          </div>
        </button>
      ) : (
        <button
          className={styles.menuButton}
          onClick={openMenu}
          aria-label="Open menu"
          type="button"
        >
          <span className={styles.menuLine} />
          <span className={styles.menuLine} />
        </button>
      )}
    </header>
  );
};

export default GameHeader;
